import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { createUri, Api } from '@api/utils';
import { Query } from './utils';
import type {
    EmployeeDto,
    EmployeeSearchResponse,
    CreateEmployeeRequest,
    UpdateEmployeeRequest,
    EmployeeSearchParams,
} from '@/types/newEmployees';

// Tags for cache invalidation
enum Tag {
    Employees = 'Employees',
    Employee = 'Employee',
}

export const employeesHooks = createApi({
    reducerPath: 'employeesApi',
    tagTypes: Object.values(Tag),
    baseQuery: fetchBaseQuery({ baseUrl: createUri(Api.Orgs, '/org/V1/management/employees') }),
    endpoints: (build) => ({
        // New search employees endpoint
        searchEmployees: build.query<EmployeeSearchResponse, { token: string } & EmployeeSearchParams>({
            query: ({ token, ...params }) => ({
                url: '/search',
                headers: { Authorization: `Bearer ${token}` },
                params,
            }),
            providesTags: (result) => [
                Tag.Employees,
                ...(result?.employees || []).map((e: EmployeeDto) => ({ type: Tag.Employee, id: e.EmployeeID })),
            ],
        }),

        createEmployee: build.mutation<{ employee: EmployeeDto }, { token: string; employee: CreateEmployeeRequest }>({
            query: ({ token, employee }) => ({
                url: '',
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: employee,
            }),
            invalidatesTags: [Tag.Employees],
        }),

        updateEmployee: build.mutation<EmployeeDto, { token: string; id: string; employee: UpdateEmployeeRequest }>({
            query: ({ token, id, employee }) => ({
                url: '',
                method: 'PATCH',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                params: { id },
                body: employee,
            }),
            invalidatesTags: (_, __, { id }) => [Tag.Employees, { type: Tag.Employee, id }],
        }),

        deleteEmployee: build.mutation<void, { token: string; id: string }>({
            query: ({ token, id }) => ({
                url: '',
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` },
                params: { id },
            }),
            invalidatesTags: (_, __, { id }) => [Tag.Employees, { type: Tag.Employee, id }],
        }),

        // Get single employee by ID
        getEmployee: build.query<EmployeeDto, { token: string; id: string }>({
            query: ({ token, id }) => ({
                url: '',
                headers: { Authorization: `Bearer ${token}` },
                params: { id },
            }),
            providesTags: (_, __, { id }) => [{ type: Tag.Employee, id }],
        }),
    }),
});

export const {
    useSearchEmployeesQuery: useSearchEmployeesQueryBase,
    useCreateEmployeeMutation,
    useUpdateEmployeeMutation,
    useDeleteEmployeeMutation,
    useGetEmployeeQuery: useGetEmployeeQueryBase,
} = employeesHooks;

// Auto-attach token to queries
export const useSearchEmployeesQuery = Query.withToken(useSearchEmployeesQueryBase);
export const useGetEmployeeQuery = Query.withToken(useGetEmployeeQueryBase);
